import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface NotificationPreference {
  id?: string
  tenant_id: string
  user_id: string
  scope: string
  scope_id: string
  push_enabled: boolean
  email_enabled: boolean
  in_app_enabled: boolean
  quiet_start: string | null
  quiet_end: string | null
  timezone: string
  min_interval_seconds: number
}

const QUERY_KEY = ['notification-preferences']

export function useNotificationPreferences() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const r = await api.get('/notification-preferences')
      const prefs: NotificationPreference[] = r.data?.preferences ?? r.data ?? []
      // Return global preference or first one.
      return prefs.find((p) => p.scope === 'global') ?? prefs[0] ?? null
    },
  })
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<NotificationPreference>) =>
      api.put('/notification-preferences', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useMuteDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { deviceId: string; durationMinutes?: number }) =>
      api.post('/notification-preferences/mute', {
        scope: 'device',
        scope_id: params.deviceId,
        duration_minutes: params.durationMinutes ?? 0,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useUnmuteDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (deviceId: string) =>
      api.post('/notification-preferences/unmute', {
        scope: 'device',
        scope_id: deviceId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useMuteRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: { ruleId: string; durationMinutes?: number }) =>
      api.post('/notification-preferences/mute', {
        scope: 'rule',
        scope_id: params.ruleId,
        duration_minutes: params.durationMinutes ?? 0,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useUnmuteRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ruleId: string) =>
      api.post('/notification-preferences/unmute', {
        scope: 'rule',
        scope_id: ruleId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}
